import styled, { css, keyframes } from 'styled-components';
import { Text } from '@oneaudi/unified-web-components';
import {
  SYS_BORDER_RADIUS_SM,
  SYS_SPACE_FIXED_150,
  SYS_FONT_SIZES_RELATIVE_75,
  SYS_FONT_SIZES_RELATIVE_85,
  SYS_FONT_SIZES_RELATIVE_100,
  SYS_TYPOGRAPHY_COPY_ORDER1_BOLD,
  SYS_TYPOGRAPHY_COPY_ORDER2_NORMAL,
  SYS_TYPOGRAPHY_COPY_ORDER3_NORMAL,
} from '@oneaudi/unified-web-alpha-common-v2';
import { COLORS } from '../../../styles/variables';
import { device } from '../../../styles/devices';

type SitewideSearchTheme = {
  sem?: {
    color?: {
      navigation?: {
        foreground?: {
          icon?: {
            interactive?: string;
          };
          text?: {
            idle?: string;
            active?: string;
          };
        };
      };
    };
  };
};

const getInteractiveIconColor = ({ theme }: { theme?: SitewideSearchTheme }): string =>
  theme?.sem?.color?.navigation?.foreground?.icon?.interactive ?? '#f2f2f2';

const getIdleTextColor = ({ theme }: { theme?: SitewideSearchTheme }): string =>
  theme?.sem?.color?.navigation?.foreground?.text?.idle ?? 'rgba(252, 252, 253, 0.7)';

const getActiveTextColor = ({ theme }: { theme?: SitewideSearchTheme }): string =>
  theme?.sem?.color?.navigation?.foreground?.text?.active ?? '#fcfcfd';

export const IconWrapper = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  position: absolute;
  right: 28px;
  top: 16px;

  width: 24px;
  height: 24px;
  /* Note: Change pointer-events to 'auto' if you want this button to be clickable! */
  pointer-events: auto;
  z-index: 2;

  svg {
    width: 100%;
    height: 100%;

    path {
      fill: none !important;
      stroke: ${process.env.IS_LEGACY ? '#333333' : '#fcfcfd'};
      stroke-opacity: ${process.env.IS_LEGACY ? 1 : 0.7};
      stroke-width: 1.5;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          color: ${getInteractiveIconColor};

          svg,
          span {
            color: inherit;
          }

          svg path {
            fill: currentColor;
            stroke: currentColor;
          }
        `
  }

  @media ${device.mobileL} {
    right: 60px;
  }
`;

export const SearchWrapper = styled.section`
  max-width: 500px;

  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          min-width: 320px;
          width: 100%;
        `
  }

  height: 100vh;
  background-color: ${process.env.IS_LEGACY ? 'white' : '#2C343F'};
  position: fixed;
  right: 0;
  top: 0;
  padding: 0;
  overflow-y: auto;
  z-index: 1000;
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  &::after {
    content: '';
    display: block;
    height: 28px;
  }
`;

export const BlackBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 999;
`;

export const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${process.env.IS_LEGACY ? 'white' : '#2C343F'};
  padding: 12px 28px 4px 28px;
`;

export const CloseWrapper = styled.div`
  padding-top: 4px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-end;

  button {
    padding-right: 0;
    padding-top: 0;
    padding-bottom: 0;
    min-height: 24px;
    align-items: flex-start;
  }

  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          button:hover {
            background-color: transparent;
            border: none;
            box-shadow: none;
          }
        `
  }

  @media ${device.mobileL} {
    padding-bottom: 28px;
  }
`;

export const SearchFieldIconWrapper = styled.div`
  position: relative;

  svg {
    opacity: 1 !important;
  }

  .sitewide-search-field > div > :first-child {
    display: none;
  }
`;

export const CustomSearchIconWrapper = styled.div`
  position: absolute;
  left: ${process.env.IS_LEGACY ? '4px' : '14px'};
  top: ${process.env.IS_LEGACY ? '8px' : '12px'};
  width: 24px;
  height: 24px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  svg {
    width: 100%;
    height: 100%;

    path {
      stroke: ${process.env.IS_LEGACY ? '#333333' : '#fcfcfd'};
      stroke-opacity: ${process.env.IS_LEGACY ? 1 : 0.7};
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }
`;

export const TabsWrapper = styled.div`
  padding: 0 16px;

  & > div > div:not(:first-child) {
    padding: 0;
    padding-top: 8px;
  }

  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          ul[role='tablist'] {
            margin-left: 16px;
          }

          li button {
            color: ${getIdleTextColor};
            text-align: center;
            font-weight: 400;
            background-color: transparent;
            box-shadow: none;
            padding-left: 0px;
            padding-right: 32px;
          }

          li button:hover {
            background: transparent !important;
            background-color: transparent !important;
            box-shadow: none !important;
          }

          li button:hover::before,
          li button:hover::after {
            background: transparent !important;
            background-color: transparent !important;
          }

          li button[aria-selected='true'],
          li button[data-active='true'] {
            color: ${getActiveTextColor};
            text-align: center;
            font-weight: 700;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        `
  }

  @media ${device.mobileL} {
    padding: 0 28px;
  }
`;

export const ListOfVehicles = styled.ul`
  padding-left: 0;
  display: flex;
  flex-direction: column;
  margin: 0;
  gap: 8px;
  & > div {
    padding: 12px;
  }
`;

export const CardWrapper = styled.a<{ vlpCard?: boolean }>`
  cursor: pointer;
  color: inherit;
  text-decoration: none;
  transition: box-shadow 0.2s ease;
  border-radius: ${process.env.IS_LEGACY ? '4px' : SYS_BORDER_RADIUS_SM};
  min-width: ${process.env.IS_LEGACY ? '343px' : '280px'};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  > div {
    height: ${({ vlpCard }) => (vlpCard ? '74px' : '124px')};
    min-width: ${process.env.IS_LEGACY ? '343px' : '280px'};
    padding: 12px;
    ${
      process.env.IS_LEGACY
        ? ''
        : css`
            background: ${
              process.env.IS_LEGACY ? 'unset' : 'linear-gradient(180deg, #232A34 0%, #181D25 100%)'
            };
          `
    }
  }

  @media ${device.mobileL} {
    width: 445px;
    > div {
      width: 445px;
      height: ${({ vlpCard }) => (vlpCard ? '78px' : '144px')};
    }
  }
`;

export const CardContentWrapper = styled.div<{ vlpCard?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 12px;

  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          svg,
          svg * {
            color: hsla(216, 33%, 99%, 0.7);
            fill: hsla(216, 33%, 99%, 0.7);
            stroke: hsla(216, 33%, 99%, 0.7);
          }
        `
  }
`;

export const ImageWrapper = styled.div`
  width: 133px;
  min-width: 133px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;

  @media ${device.mobileL} {
    width: 158px;
    min-width: 158px;
    height: 120px;
  }
`;

export const Image = styled.img`
  width: 100%;
`;
export const VehicleData = styled.div<{ gap?: number; vlpCard?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => (gap ? `${gap}px` : '12px')};

  @media ${device.mobileL} {
    gap: ${({ gap }) => (gap ? `${gap}px` : process.env.IS_LEGACY ? '24px' : SYS_SPACE_FIXED_150)};
  }
`;

export const NameMileage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .mileage {
    color: #666;
  }
`;

export const ClampedText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  ${
    process.env.IS_LEGACY
      ? css`
          font-size: 14px;

          @media ${device.mobileL} {
            font-size: 16px;
          }
        `
      : css`
          color: ${getIdleTextColor};
          font-size: ${SYS_FONT_SIZES_RELATIVE_85};
          font-style: normal;
          font-weight: 400;
          line-height: ${SYS_TYPOGRAPHY_COPY_ORDER2_NORMAL.lineHeight};
          letter-spacing: ${SYS_TYPOGRAPHY_COPY_ORDER2_NORMAL.letterSpacing};
        `
  }
`;

const TextBase = Text || 'span';

export const UsedVehicleText = styled(TextBase)`
  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          color: ${getIdleTextColor};
          font-size: ${SYS_FONT_SIZES_RELATIVE_75};
          font-style: normal;
          font-weight: 400;
          line-height: ${SYS_TYPOGRAPHY_COPY_ORDER3_NORMAL.lineHeight};
          letter-spacing: ${SYS_TYPOGRAPHY_COPY_ORDER3_NORMAL.letterSpacing};
        `
  }
`;

export const PriceText = styled(TextBase)`
  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          color: ${getActiveTextColor};
          font-size: ${SYS_FONT_SIZES_RELATIVE_100};
          font-style: normal;
          font-weight: 700;
          line-height: ${SYS_TYPOGRAPHY_COPY_ORDER1_BOLD.lineHeight};
          letter-spacing: ${SYS_TYPOGRAPHY_COPY_ORDER1_BOLD.letterSpacing};
          font-stretch: unset;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        `
  }
`;

export const RedirectCta = styled.div``;

export const ViewAllResults = styled.div`
  display: flex;
  flex-direction: row;
`;

export const NoResultsWrapper = styled.section`
  padding: 28px 16px 0 16px;

  ${
    process.env.IS_LEGACY
      ? ''
      : css`
          a[data-testid^='no-results-'] > .no-results-card-inner {
            background: linear-gradient(180deg, #232a34 0%, #181d25 100%);
            margin-bottom: 8px;
          }

          a[data-testid^='no-results-'] > .no-results-card-inner svg,
          a[data-testid^='no-results-'] > .no-results-card-inner svg * {
            color: hsla(216, 33%, 99%, 0.7);
            fill: hsla(216, 33%, 99%, 0.7);
            stroke: hsla(216, 33%, 99%, 0.7);
          }
        `
  }

  @media ${device.mobileL} {
    padding: 28px 28px 0 28px;
  }
`;
export const NoResultsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 28px;
`;

//------------------------- Spinner ----------------------//
interface StyleProps {
  size: number | undefined;
}

const spin = keyframes`
  to { transform: rotate(360deg) }
`;

export const SpinnerWrapper = styled.div`
  height: 70%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledSpinner = styled.div<StyleProps>`
  display: inline-block;
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
  border: 3px solid ${COLORS.grey15};
  border-radius: 50%;
  border-top-color: ${COLORS.audiRed};
  animation: ${spin} 1s ease-in-out infinite;
`;

export const LoadingSkeletonWrapper = styled.div`
  padding: 16px 16px 0 16px;

  @media ${device.mobileL} {
    padding: 16px 28px 0 28px;
  }
`;

export const SkeletonTabsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  width: 85%;
`;

export const SkeletonTabPill = styled.div`
  height: 32px;
  flex: 1;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
`;

export const SkeletonCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px;
  margin-bottom: 12px;
  min-height: 124px;
  border-radius: ${process.env.IS_LEGACY ? '4px' : SYS_BORDER_RADIUS_SM};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.03);

  @media ${device.mobileL} {
    min-height: 144px;
  }
`;

export const SkeletonImage = styled.div`
  width: 133px;
  min-width: 133px;
  height: 100px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);

  @media ${device.mobileL} {
    width: 158px;
    min-width: 158px;
    height: 120px;
  }
`;

export const SkeletonContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  padding-top: 2px;
`;

export const SkeletonLine = styled.div<{ width?: string }>`
  height: 16px;
  width: ${({ width }) => width ?? '100%'};
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);

  &:last-child {
    width: 56%;
  }
`;
